import { useEffect, useState } from "react";
import clsx from "clsx";
import FloorplanHeader from "../floorplans/blocks/floorplanHeader";
import styles from "./sightmap.module.scss";
import { useNavigate } from "react-router-dom";
import fetchData from "../../utils/fetchData";
import CommunityCostGuide from "../floorplans/blocks/communityCostGuide";

export default function Sightmap() {
  const [activeTab, setActiveTab] = useState("map");
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(window.location.search);
  const unitId = searchParams.get("unitid");
  const [data, setData] = useState();
  const [error, setError] = useState(null);

  let units = [];
  units.push(unitId);

  useEffect(() => {
    const fetchSightmap = async () => {
      try {
        setIsLoading(true);
        const response = await fetchData("/wp-json/entrata/v1/getFloorPlan");
        if (response?.config?.sightmap_url) {
          setData(response);
          setIsLoading(false);
        } else {
          navigate("/floor-plans");
        }
      } catch (err) {
        setError(err.message);
        setIsLoading(false);
      }
    };

    fetchSightmap();
  }, []);



  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab !== "map") {
      navigate(`/floor-plans`);
      localStorage.setItem("activeTab", tab);
    }
  };

  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 0
  );

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    if (typeof window !== "undefined") {
      window.addEventListener("resize", handleResize);
    }

    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("resize", handleResize);
      }
    };
  }, [windowWidth, activeTab]);

  // This effect handles making spans in the total cost description clickable
  // It finds all span elements and makes them scroll to the community cost guide section
  const handleTotalCostDescriptionClick = (e) => {
    if (e.target.closest('span.bottom-scroll-link')) {
      document.getElementById('community-cost-guide')?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const gtagId = process.env.REACT_APP_GTAG_ID || "";
    const gtagScript = document.createElement("script");
    gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${gtagId}`;
    gtagScript.id = "gtag-js";
    gtagScript.async = true;
    document.head.appendChild(gtagScript);

    gtagScript.onload = () => {
      window.dataLayer = window.dataLayer || [];
      window.gtag = function () {
        window.dataLayer.push(arguments);
      };
      window.gtag("js", new Date());
      window.gtag("config", gtagId);
    };

    const initializeSightMap = () => {
      if (window.SightMap && data?.config?.sightmap_url) {
        const embed = new window.SightMap.Embed("sightmap");
        console.log(embed);

        embed.on("ready", function () {
          console.log("SightMap is ready");

          const gtagId = process.env.REACT_APP_GTAG_ID || "";
          const logEvent = (eventName) => {
            if (typeof window.gtag === "function") {
              window.gtag("event", eventName, { send_to: gtagId });
            } else {
              console.error("gtag is not loaded");
            }
            console.log(eventName);
          };

          embed.on("metrics.unitDetails.apply.click", (event) => {
            const unitNumber = event.data.unit.unitNumber;
            logEvent(`Apply Click - Unit ${unitNumber}`);
          });

          embed.on("metrics.unitList.unit.click", (event) => {
            const unitNumber = event.data.unit.unitNumber;
            logEvent(`Unit List Click - Unit ${unitNumber}`);
          });

          embed.on("metrics.unitMap.unit.click", (event) => {
            const unitNumber = event.data.unit.unitNumber;
            logEvent(`Unit Map Click - Unit ${unitNumber}`);
          });

          embed.on("metrics.unitMatches.impression", () => {
            logEvent("SightMap Unit Matches View");
          });

          embed.on("metrics.unitList.impression", () => {
            logEvent("SightMap Unit List View");
          });

          embed.on("metrics.unitList.change", () => {
            logEvent("SightMap Unit List Change");
          });

          embed.on("metrics.unitDetails.outbound.click", (event) => {
            const unitNumber = event.data.unit.unitNumber;
            const ctaName = event.data.label;
            logEvent(`CTA Click - ${ctaName} - Unit ${unitNumber}`);
          });
        });
      } else {
        console.error(
          "SightMap library is not loaded or data is not available"
        );
      }
    };

    const script = document.createElement("script");
    script.src = "https://sightmap.com/embed/api.js?ver=6.4.3";
    script.id = "sightmap-js";
    script.async = true;

    // Only initialize SightMap after the script is loaded
    script.onload = () => {
      if (data?.config?.sightmap_url) {
        initializeSightMap();
      }
    };

    document.head.appendChild(script);

    return () => {
      const existingScript = document.getElementById("sightmap-js");
      if (existingScript) {
        document.head.removeChild(existingScript);
      }

      const gatagJS = document.getElementById("gtag-js");
      if (gatagJS) {
        document.head.removeChild(gatagJS);
      }
    };
  }, [data]);

  return (
    <div id="sightmap-template" className="grid">
      <FloorplanHeader />
      <div className={styles.floorplansFilters}>
        <div className={styles.tabs}>
          {["grid", "map"].map((view) => (
            <button
              key={view}
              id={`${view}ViewBtn`}
              className={clsx(
                styles[`${view}ViewBtn`],
                activeTab === view && styles.active
              )}
              onClick={() => handleTabChange(view)}
            >
              {view === "grid" ? "Floor Plan View" : "Map View"}
            </button>
          ))}
        </div>

        {data?.community_cost_guide?.total_cost_clarity_title && data?.community_cost_guide?.total_cost_clarity_description && (
          <div className={styles.totalCost}>
            <h3>{data.community_cost_guide.total_cost_clarity_title}</h3>
            <div onClick={handleTotalCostDescriptionClick}>
              <p dangerouslySetInnerHTML={{ __html: data.community_cost_guide.total_cost_clarity_description }} />
            </div>
          </div>
        )}

        <div className={styles.sightmap}>
          {isLoading ? (
            <div className={styles.loading}>Loading...</div>
          ) : data?.config?.sightmap_url ? (
            <iframe
              title="Sightmap"
              id="sightmap"
              allow="clipboard-write; clipboard-read; web-share"
              src={`${data.config.sightmap_url}?enable_api=1&origin=${process.env.REACT_APP_ORIGIN_URL || window.location.origin
                }`}
            />
          ) : null}
        </div>
      </div>

      <CommunityCostGuide data={data} />
    </div>
  );
}
